/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { formatDocForDisplay, onlyDigits } from '../../../utils/format';

// ---------------------------------------------------------------------------
// Seleção múltipla de EMPRESA EMISSORA (design handoff §1.4).
//
// Substituiu a lista de checkbox-cards: com poucas contas ela cabia, mas não
// escala — cada CNPJ novo empurrava o resto do formulário para baixo. Aqui o
// gatilho ocupa uma linha fixa e a lista vive num painel com busca por nome ou
// CNPJ.
//
// Contas INATIVAS aparecem, marcadas como tal: conta desativada continua tendo
// histórico de cobranças, e escondê-la tornaria esses boletos infiltráveis.
// Filtrar histórico é diferente de emitir.
//
// Multi-seleção NÃO fecha o painel (o operador costuma marcar duas de uma vez);
// fecha em "Concluir" ou clique fora.
// ---------------------------------------------------------------------------

const Wrap = styled.div`
   position: relative;
   max-width: 420px;
`;

const Trigger = styled.button`
   width: 100%;
   height: 40px;
   border-radius: 10px;
   border: 1px solid rgba(0, 0, 0, 0.12);
   background: #fff;
   font-family: inherit;
   font-size: 14px;
   font-weight: 600;
   color: #111;
   padding: 0 12px;
   cursor: pointer;
   display: flex;
   align-items: center;
   gap: 8px;

   &:hover {
      border-color: rgba(0, 0, 0, 0.22);
   }
   &:focus-visible {
      outline: none;
      border-color: #4098ef;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }

   .label {
      flex: 1;
      min-width: 0;
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }

   .count {
      flex: 0 0 auto;
      font-size: 11px;
      font-weight: 800;
      color: #0b3a63;
      background: rgba(64, 152, 239, 0.16);
      border-radius: 999px;
      padding: 2px 8px;
   }

   .chevron {
      flex: 0 0 auto;
      color: #8a8a8a;
      font-size: 12px;
   }
`;

const Panel = styled.div`
   position: absolute;
   top: 46px;
   left: 0;
   width: 100%;
   min-width: 300px;
   z-index: 20;
   background: #fff;
   border-radius: 12px;
   border: 1px solid rgba(0, 0, 0, 0.12);
   box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
   animation: mwaMenuFade 0.14s ease;

   @keyframes mwaMenuFade {
      from {
         opacity: 0;
         transform: translateY(4px);
      }
      to {
         opacity: 1;
         transform: translateY(0);
      }
   }
`;

const PanelSearch = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 10px 12px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.07);

   .icon {
      color: #9a9a9a;
      display: inline-flex;
   }

   input {
      flex: 1;
      min-width: 0;
      border: 0;
      outline: none;
      font-family: inherit;
      font-size: 13px;
      color: #111;
      background: transparent;

      &::placeholder {
         color: #9a9a9a;
      }
   }
`;

const List = styled.div`
   max-height: 220px;
   overflow-y: auto;
   padding: 6px;
`;

const Item = styled.button`
   width: 100%;
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 8px 10px;
   border: 0;
   border-radius: 8px;
   background: ${({ $selected }) =>
      $selected ? 'rgba(64,152,239,0.1)' : 'transparent'};
   color: ${({ $selected }) => ($selected ? '#0b3a63' : '#111')};
   font-family: inherit;
   cursor: pointer;
   text-align: left;

   &:hover {
      background: ${({ $selected }) =>
         $selected ? 'rgba(64,152,239,0.14)' : '#f5f5f5'};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }

   .box {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      border-radius: 5px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      line-height: 1;
      color: #fff;
      background: ${({ $selected }) => ($selected ? '#4098ef' : '#fff')};
      border: ${({ $selected }) =>
         $selected ? '1px solid #4098ef' : '1px solid rgba(0,0,0,0.25)'};
   }

   .text {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
   }

   .name {
      font-size: 12.5px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }

   .doc {
      font-size: 11px;
      font-weight: 600;
      color: #8a8a8a;
      font-variant-numeric: tabular-nums;
   }

   .inactive {
      font-size: 10px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #854f0b;
   }
`;

const EmptyItem = styled.div`
   padding: 12px 10px;
   font-size: 12px;
   font-weight: 600;
   color: #8a8a8a;
`;

const PanelFoot = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 8px 10px;
   background: #fafafa;
   border-top: 1px solid rgba(0, 0, 0, 0.07);
   border-radius: 0 0 12px 12px;

   button {
      height: 30px;
      padding: 0 12px;
      border-radius: 8px;
      font-family: inherit;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      border: 0;
      background: transparent;

      &:focus-visible {
         outline: none;
         box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
      }
   }

   .all {
      color: #2f7bd8;
   }
   .clear {
      color: #8a8a8a;
   }
   .done {
      margin-left: auto;
      background: ${({ theme }) => theme?.colors?.primarySoft ?? '#96c4f0'};
      color: #0b3a63;
      font-weight: 800;
   }
`;

export default function BankAccountMultiSelect({
   accounts,
   selectedIds,
   onChange,
   disabled = false,
}) {
   const [open, setOpen] = useState(false);
   const [query, setQuery] = useState('');
   const wrapRef = useRef(null);

   // Fecha ao clicar fora. `mousedown` (e não `click`) para o painel sumir já
   // no apertar do botão, sem piscar junto com o clique de outro controle.
   useEffect(() => {
      if (!open) return undefined;
      const onMouseDown = (event) => {
         if (!wrapRef.current?.contains(event.target)) setOpen(false);
      };
      document.addEventListener('mousedown', onMouseDown);
      return () => document.removeEventListener('mousedown', onMouseDown);
   }, [open]);

   // Abrir/fechar zera a busca — reabrir com filtro antigo esconde opções e
   // parece lista vazia.
   useEffect(() => {
      setQuery('');
   }, [open]);

   const visible = useMemo(() => {
      const term = query.trim().toLowerCase();
      if (!term) return accounts;
      const digits = onlyDigits(term);
      return accounts.filter((account) => {
         const byName = (account.account_name ?? '')
            .toLowerCase()
            .includes(term);
         const byOwner = (account.account_owner_name ?? '')
            .toLowerCase()
            .includes(term);
         const byDoc =
            digits.length > 0 &&
            onlyDigits(account.account_doc_number).includes(digits);
         return byName || byOwner || byDoc;
      });
   }, [accounts, query]);

   const label = useMemo(() => {
      if (!selectedIds.length) return 'Todas as empresas';
      if (selectedIds.length === 1) {
         const found = accounts.find(
            (account) => String(account.id) === selectedIds[0]
         );
         return found?.account_name ?? '1 empresa selecionada';
      }
      return `${selectedIds.length} empresas selecionadas`;
   }, [selectedIds, accounts]);

   const toggle = (id) => {
      const key = String(id);
      onChange(
         selectedIds.includes(key)
            ? selectedIds.filter((item) => item !== key)
            : [...selectedIds, key]
      );
   };

   return (
      <Wrap ref={wrapRef}>
         <Trigger
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
         >
            <span className="label">{label}</span>
            {selectedIds.length > 1 && (
               <span className="count">{selectedIds.length}</span>
            )}
            <span className="chevron" aria-hidden="true">
               ▾
            </span>
         </Trigger>

         {open && (
            <Panel role="listbox">
               <PanelSearch>
                  <span className="icon" aria-hidden="true">
                     ⌕
                  </span>
                  <input
                     type="text"
                     value={query}
                     onChange={(e) => setQuery(e.target.value)}
                     placeholder="Buscar por nome ou CNPJ..."
                     aria-label="Buscar empresa"
                  />
               </PanelSearch>

               <List>
                  {visible.length ? (
                     visible.map((account) => {
                        const key = String(account.id);
                        const selected = selectedIds.includes(key);
                        return (
                           <Item
                              key={key}
                              type="button"
                              $selected={selected}
                              onClick={() => toggle(account.id)}
                              role="option"
                              aria-selected={selected}
                           >
                              <span className="box" aria-hidden="true">
                                 {selected ? '✓' : ''}
                              </span>
                              <span className="text">
                                 <span className="name">
                                    {account.account_name}
                                    {account.status === 'inactive' && (
                                       <>
                                          {' '}
                                          <span className="inactive">
                                             inativa
                                          </span>
                                       </>
                                    )}
                                 </span>
                                 <span className="doc">
                                    {formatDocForDisplay(
                                       account.account_doc_number
                                    )}
                                 </span>
                              </span>
                           </Item>
                        );
                     })
                  ) : (
                     <EmptyItem>Nenhuma empresa encontrada.</EmptyItem>
                  )}
               </List>

               <PanelFoot>
                  <button
                     type="button"
                     className="all"
                     onClick={() =>
                        onChange(accounts.map((account) => String(account.id)))
                     }
                  >
                     Todas
                  </button>
                  <button
                     type="button"
                     className="clear"
                     onClick={() => onChange([])}
                  >
                     Limpar
                  </button>
                  <button
                     type="button"
                     className="done"
                     onClick={() => setOpen(false)}
                  >
                     Concluir
                  </button>
               </PanelFoot>
            </Panel>
         )}
      </Wrap>
   );
}

BankAccountMultiSelect.propTypes = {
   accounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
   // Ids como STRING — é o que vem da URL; comparar sempre no mesmo tipo evita
   // o clássico 1 !== '1'.
   selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
   onChange: PropTypes.func.isRequired,
   disabled: PropTypes.bool,
};
